import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ShieldCheck } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1620203853151-496c7228306c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxzdGFpbmxlc3MlMjBzdGVlbCUyMGluZHVzdHJpYWwlMjBtYWNoaW5lfGVufDB8fHx8MTc4Nzg1MTU3NHww&ixlib=rb-4.1.0&q=85";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/75" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <img src="/thermaco-logo.webp" alt="Thermaco" className="h-9 bg-white rounded-sm px-2 py-1" />
          </div>
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
              Pricing Portal
            </h1>
            <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
              Import NetSuite price lists, manage per-SKU assets, and give every customer a
              private link to their own pricing — no PDFs, no leaks.
            </p>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Internal Admin Access</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <img src="/thermaco-logo.webp" alt="Thermaco" className="h-9" />
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Admin sign in</h2>
          <p className="text-sm text-slate-500 mt-1 mb-8">Manage pricing and customer access.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.1em] text-slate-500">Email</Label>
              <Input
                id="email" type="email" value={email} required
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm" placeholder="you@thermaco.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.1em] text-slate-500">Password</Label>
              <Input
                id="password" type="password" value={password} required
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm" placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit" disabled={busy}
              className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 text-white font-medium transition-colors"
              data-testid="login-submit-button"
            >
              <Lock className="h-4 w-4 mr-2" />
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
